import { useEffect, useMemo, useState } from "react";
import { Divider } from "@mui/material";

import AmountOdometer from "../../Components/Others/amount-odometer";

import renderAvailableBalance from "../../Organisms/Modals/Banking/available-balance";
import renderCurrentBalance from "../../Organisms/Modals/Banking/current-balance";
import renderAverageMonthlyBalance from "../../Organisms/Modals/Banking/average-monthly-balance";

import { useModalContext } from "../../Contexts/ModalContext";

import {
  formatCurrency,
  formatNumberWithCentsToUSD,
  getAnimateAvailableBalanceFromKey,
  isSVDApproved,
  LocalStorage,
} from "../../Utils/helpers";
import { maskAccountNumber } from "../../Utils/pii";

import { Help, Eye, EyeSlash } from "../../Assets/Icons";

export default function BankAccountInformation({ currentBalance, availableBalance, stakeDebit = {} }) {
  const modalContext = useModalContext();

  const [showFullAccount, setShowFullAccount] = useState(false);
  const toggleAccountVisibility = () => {
    setShowFullAccount(!showFullAccount);
  };

  const IsSVDApproved = useMemo(() => {
    return isSVDApproved(stakeDebit?.data);
  }, [stakeDebit?.data]);

  const animateAvailableBalanceFrom = useMemo(
    () => parseFloat(LocalStorage.read(getAnimateAvailableBalanceFromKey()) ?? 0),
    []
  );

  useEffect(() => {
    const timeout = setTimeout(
      () =>
        LocalStorage.write(
          getAnimateAvailableBalanceFromKey(),
          IsSVDApproved ? availableBalance / 100 : currentBalance
        ),
      2000
    );
    return () => clearTimeout(timeout);
  }, [IsSVDApproved, availableBalance, currentBalance]);

  const accountNumber = useMemo(() => {
    const fullNumber = stakeDebit?.data?.data?.account_number || "XXXXXXXXXXXX";
    return showFullAccount ? fullNumber : maskAccountNumber(fullNumber, { wrap: false });
  }, [stakeDebit?.data?.data?.account_number, showFullAccount]);

  const routingNumber = useMemo(() => {
    return stakeDebit?.data?.data?.routing_number ?? "012341239";
  }, [stakeDebit?.data?.data?.routing_number]);

  let accountInformation = <></>;

  if (IsSVDApproved) {
    accountInformation = (
      <>
        <Divider />
        <div className="account-info">
          <div className="account-number">
            <span className="label">Account</span>
            <span className="value">
              {accountNumber}
              <button className="icon" onClick={toggleAccountVisibility}>
                {showFullAccount ? <EyeSlash /> : <Eye />}
              </button>
            </span>
          </div>
          <Divider orientation="vertical" flexItem />
          <div className="routing-number">
            <span className="label">Routing</span>
            <span className="value">{routingNumber}</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="banking-account-info">
      <div className="balance-container">
        <p className="balance-label">
          Available Balance
          <button className="icon" onClick={renderAvailableBalance.bind(this, modalContext)}>
            <Help />
          </button>
        </p>
        <AmountOdometer
          className="mt-1"
          initialValue={animateAvailableBalanceFrom}
          value={IsSVDApproved ? availableBalance / 100 : currentBalance}
          size="40px"
          fontWeight={600}
          color="var(--mist)"
        />
        {IsSVDApproved && (
          <>
            <p className="available-balance mt-2">
              Current balance: {formatCurrency(currentBalance)}
              <button className="icon" onClick={renderCurrentBalance.bind(this, modalContext)}>
                <Help />
              </button>
            </p>
            <p className="available-balance mt-1">
              Avg monthly balance: {formatNumberWithCentsToUSD(stakeDebit?.data?.data?.avg_monthly_balance ?? 0)}
              <button className="icon" onClick={renderAverageMonthlyBalance.bind(this, modalContext)}>
                <Help />
              </button>
            </p>
          </>
        )}
      </div>

      {accountInformation}
    </div>
  );
}
